import React, { useEffect, useMemo, useRef, useState } from "react";
import { subDays, endOfDay, format, differenceInCalendarDays } from "date-fns";
import { Button, Form } from "react-bootstrap";
import "./customDatePicker.css";

const CustomDateRangeFilter = ({ onChange, isByInterval, showDates }) => {
  const [showDateRange, setShowDateRange] = useState(false);
  const defaultStartDate = subDays(new Date(), 29);
  const defaultEndDate = endOfDay(new Date());
  const defaultRangeLabel = "Last 30 Days";

  const [interval, setInterval] = useState("week");
  const presetRanges = useMemo(
    () => [
      { key: "last7Days", label: "Last 7 Days", days: 7 },
      { key: "last21Days", label: "Last 21 Days", days: 21 },
      { key: "last30Days", label: "Last 30 Days", days: 30 },
      { key: "last60Days", label: "Last 60 Days", days: 60 },
      { key: "last90Days", label: "Last 90 Days", days: 90 },
      { key: "last120Days", label: "Last 120 Days", days: 120 },
    ],
    []
  );

  const initialSelection = useMemo(
    () => ({
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      key: "selection",
      label: defaultRangeLabel,
    }),
    [defaultStartDate, defaultEndDate]
  );

  const getInterval = (startDate, endDate) => {
    const days = Math.max(differenceInCalendarDays(endDate, startDate), 0);
    if (days <= 7) return "day";
    if (days <= 31) return "week";
    return "month";
  };

  const [state, setState] = useState([initialSelection]);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    const temp = getInterval(initialSelection.startDate, initialSelection.endDate);
    onChange(initialSelection, temp);
  }, [initialSelection, onChange]);

  useEffect(() => {
    const temp = getInterval(state[0].startDate, state[0].endDate);
    setInterval(temp);
  }, [state]);

  const getCurrentLabel = (startDate, endDate) => {
    const totalDays = differenceInCalendarDays(endDate, startDate) + 1;
    const matchedPreset = presetRanges.find((preset) => preset.days === totalDays);
    return matchedPreset ? matchedPreset.label : "Custom Period";
  };

  const updateSelection = (nextStartDate, nextEndDate, customKey, label) => {
    const safeStartDate = nextStartDate > nextEndDate ? nextEndDate : nextStartDate;
    const safeEndDate = nextEndDate < nextStartDate ? nextStartDate : nextEndDate;
    setState([
      {
        startDate: safeStartDate,
        endDate: safeEndDate,
        key: "selection",
        customKey,
        label,
      },
    ]);
  };

  const handlePresetSelect = (preset) => {
    const nextEndDate = endOfDay(new Date());
    const nextStartDate = subDays(nextEndDate, preset.days - 1);
    updateSelection(nextStartDate, nextEndDate, preset.key, preset.label);
  };

  const handleDateFieldChange = (field, value) => {
    if (!value) return;
    const changedDate = new Date(`${value}T00:00:00`);
    if (Number.isNaN(changedDate.getTime())) return;
    const startDate = field === "startDate" ? changedDate : state[0].startDate;
    const endDate = field === "endDate" ? endOfDay(changedDate) : state[0].endDate;
    const label = getCurrentLabel(startDate, endDate);
    updateSelection(startDate, endDate, label === "Custom Period" ? "customPeriod" : state[0].customKey, label);
  };

  const DateContainer = ({ data }) => {
    return <span>{format(data, "MM/dd/yyyy")}</span>;
  };

  const toInputDate = (data) => format(data, "yyyy-MM-dd");
  const isCustomPeriodSelected = state[0]?.label === "Custom Period";

  const ApplyButton = () => {
    return (
      <div className="date-range-picker-action-container">
        <Button
          //className="btn btn-transparent text-primary rounded-0 px-4 mr-2"
          onClick={() => {
            const nextInterval = getInterval(state[0].startDate, state[0].endDate);
            onChange(state[0], nextInterval);
            setShowDateRange(false);
          }}
        >
          Apply
        </Button>
      </div>
    );
  };

  const handleInterval = (e) => {
    setInterval(e.target?.value);
    if (!showDateRange) {
      onChange(state[0], e.target?.value);
    }
  };

  return (
    <>
      <div style={{ display: "flex" }}>
        <div>
          <Button
            className="date-range-action"
            variant="outline-primary"
            onClick={() => {
              setShowDateRange((prev) => !prev);
            }}
          >
            {!!state[0]?.label ? state[0]?.label : "Last 30 days"}
          </Button>
        </div>
        {showDates && (
          <div className="date-container">
            <div className="date-item start-date-container">
              Start Date: <DateContainer data={state[0]?.startDate} />
            </div>
            <div className="date-item end-date-container">
              End Date: <DateContainer data={state[0]?.endDate} />
            </div>
          </div>
        )}
        {!!isByInterval && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Form.Label style={{ margin: "0 8px 0 0" }}>Interval</Form.Label>
            <Form.Select
              aria-label="Default select example"
              value={interval}
              onChange={(e) => {
                handleInterval(e);
              }}
            >
              <option value="">Select Interval</option>
              <option value="day">
                By Day
              </option>
              <option value="week">
                By Week
              </option>
              <option value="month">
                By Month
              </option>
            </Form.Select>
          </div>
        )}
      </div>
      {!!showDateRange && (
        <div className="date-range-picker-custom-container">
          <div className="date-range-picker-fallback">
            <div className="date-range-picker-header">
              <h6>Select Date Range</h6>
              <span>
                {format(state[0]?.startDate, "MMM dd, yyyy")} - {format(state[0]?.endDate, "MMM dd, yyyy")}
              </span>
            </div>
            {isCustomPeriodSelected && (
              <div className="date-range-picker-fields-card">
                <div className="date-range-picker-fields-heading">Custom Period</div>
                <div className="date-range-picker-fields">
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={toInputDate(state[0].startDate)}
                      onChange={(event) => handleDateFieldChange("startDate", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={toInputDate(state[0].endDate)}
                      onChange={(event) => handleDateFieldChange("endDate", event.target.value)}
                    />
                  </Form.Group>
                </div>
              </div>
            )}
            <div className="date-range-picker-scope-title">Scope</div>
            <div className="date-range-picker-presets">
              {presetRanges.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className={`date-range-picker-preset-btn ${
                    state[0]?.label === preset.label ? "active" : ""
                  }`}
                  onClick={() => handlePresetSelect(preset)}
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                className={`date-range-picker-preset-btn ${
                  state[0]?.label === "Custom Period" ? "active" : ""
                }`}
                onClick={() =>
                  updateSelection(
                    state[0].startDate,
                    state[0].endDate,
                    "customPeriod",
                    "Custom Period"
                  )
                }
              >
                Custom Period
              </button>
            </div>
            <ApplyButton />
          </div>
        </div>
      )}
    </>
  );
};

export default CustomDateRangeFilter;
